import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Form, Formik } from "formik";
import { Autocomplete, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import FormField from "components/FormField";
import { ErrorBundary } from "config/ErrorBundary/error-bundary";
import FormErrorHandler from "handlers/FormErrorHandler/form-error.handler";
import validation from "./schemas/validation";
import formularioProf from "./schemas/form";
import initialValues from "./schemas/initialValues";

const AsignaturaProfesorForm = ({
  idAsig,
  opcionesAsigAll,
  opcionesAsig,
  opcionesProf,
  profesorAsignaturaStash,
  setProfesorAsignaturaStash,
  handleSubmit,
  handleClose,
  isLoading,
  oldValues,
}) => {
  const { formField, formId } = formularioProf;
  const currentValidation = validation[0];

  return (
    <Formik
      initialValues={{
        ...initialValues,
        distribucion_grupo: oldValues?.distribucion_grupo || "",
        asignatura: oldValues?.asignatura || "",
        profesor: oldValues?.profesor || "",
      }}
      enableReinitialize
      validationSchema={currentValidation}
      onSubmit={(values, actions) => {
        handleSubmit(values, actions);
      }}
    >
      {({ setFieldValue, values, errors, touched, isSubmitting }) => {
        const asignaturaValue = useMemo(() => {
          const filteredOptions = idAsig
            ? opcionesAsigAll
            : opcionesAsig?.filter(
                (asig) =>
                  !profesorAsignaturaStash.some(
                    (item) => item.asignatura === asig.url,
                  ),
              );

          return (
            filteredOptions?.find(
              (opcion) => opcion.url === values.asignatura,
            ) || null
          );
        }, [
          idAsig,
          opcionesAsigAll,
          opcionesAsig,
          profesorAsignaturaStash,
          values.asignatura,
        ]);

        const profesorValue = useMemo(() => {
          return (
            opcionesProf?.find((opcion) => opcion.url === values.profesor) ||
            null
          );
        }, [opcionesProf, values.profesor]);

        return (
          <ErrorBundary renderOnError={() => <FormErrorHandler />}>
            <Form id={`${formId}-asig`} autoComplete="off">
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Autocomplete
                    id="distribuir-asignatura"
                    options={
                      idAsig
                        ? opcionesAsigAll || []
                        : opcionesAsig?.filter(
                            (asig) =>
                              !profesorAsignaturaStash.some(
                                (item) => item.asignatura === asig.url,
                              ),
                          ) || []
                    }
                    onChange={(e, value) => {
                      setFieldValue(
                        formField.asignatura.name,
                        value ? value.url : null,
                      );
                    }}
                    value={asignaturaValue}
                    isOptionEqualToValue={(option, value) =>
                      option.url === value.url
                    }
                    getOptionLabel={(option) =>
                      `${option.nombre} ${option.key}`
                    }
                    renderInput={(params) => (
                      <FormField
                        id="distribucionA"
                        name={formField.asignatura.name}
                        label="Asignatura"
                        {...params}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    id="distribuir-profesor"
                    options={opcionesProf || []}
                    onChange={(e, value) => {
                      setFieldValue(
                        formField.profesor.name,
                        value ? value.url : null,
                      );
                    }}
                    value={profesorValue}
                    isOptionEqualToValue={(option, value) =>
                      option.url === value.url
                    }
                    getOptionLabel={(option) => `${option.nombre}`}
                    renderInput={(params) => (
                      <FormField
                        id="distribucionP"
                        name={formField.profesor.name}
                        label="Profesor"
                        {...params}
                      />
                    )}
                  />
                </Grid>
              </Grid>

              {!idAsig && (
                <MDBox mt={3} width="100%">
                  <MDButton
                    variant="contained"
                    color="dark"
                    type="button"
                    fullWidth
                    onClick={() => {
                      if (
                        values.distribucion_grupo &&
                        values.profesor &&
                        values.asignatura
                      ) {
                        setProfesorAsignaturaStash((prev) => [
                          ...prev,
                          {
                            distribucion_grupo: values.distribucion_grupo,
                            profesor: values.profesor,
                            asignatura: values.asignatura,
                          },
                        ]);
                        setFieldValue("asignatura", "");
                        setFieldValue("profesor", "");
                      }
                    }}
                  >
                    Agregar +
                  </MDButton>
                </MDBox>
              )}

              {/* Lista de asignaciones agregadas */}
              {!idAsig && profesorAsignaturaStash.length > 0 && (
                <MDBox
                  mt={2}
                  sx={{
                    maxHeight: 220,
                    overflowY: "auto",
                    p: 1,
                    backgroundColor: "#f8fafc",
                    borderRadius: 2,
                  }}
                >
                  <MDTypography
                    variant="subtitle2"
                    fontWeight="bold"
                    color="info"
                    mb={1}
                  >
                    Va a distribuir a este grupo:
                  </MDTypography>
                  <Grid container spacing={1}>
                    {profesorAsignaturaStash.map((item, idx) => {
                      const asigObj =
                        opcionesAsigAll?.find(
                          (a) => a.url === item.asignatura,
                        ) ||
                        opcionesAsig?.find((a) => a.url === item.asignatura);
                      const profObj = opcionesProf?.find(
                        (p) => p.url === item.profesor,
                      );

                      return (
                        <Grid item xs={12} key={idx}>
                          <MDBox
                            sx={{
                              background: "#fff",
                              borderRadius: 2,
                              boxShadow: 1,
                              p: 1.5,
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <MDBox display="flex" flexDirection="column">
                              <MDTypography
                                variant="button"
                                fontWeight="medium"
                                color="text"
                              >
                                Asignatura:{" "}
                                {asigObj
                                  ? `${asigObj.nombre} ${asigObj.key}`
                                  : item.asignatura}
                              </MDTypography>
                              <MDTypography variant="button" color="text">
                                Profesor:{" "}
                                {profObj ? profObj.nombre : item.profesor}
                              </MDTypography>
                            </MDBox>
                            <MDButton
                              variant="text"
                              color="error"
                              iconOnly
                              onClick={() => {
                                setProfesorAsignaturaStash((prev) =>
                                  prev.filter((_, i) => i !== idx),
                                );
                              }}
                            >
                              <span style={{ fontSize: "1.2rem" }}>➖</span>
                            </MDButton>
                          </MDBox>
                        </Grid>
                      );
                    })}
                  </Grid>
                </MDBox>
              )}

              <MDBox mt={4} display="flex" justifyContent="center" gap={2}>
                <MDButton
                  variant="outlined"
                  color="info"
                  onClick={handleClose}
                  disabled={isSubmitting || isLoading}
                >
                  Cancelar
                </MDButton>
                <MDButton
                  type="submit"
                  variant="contained"
                  color="info"
                  disabled={isSubmitting || isLoading}
                >
                  Aceptar
                </MDButton>
              </MDBox>
            </Form>
          </ErrorBundary>
        );
      }}
    </Formik>
  );
};

AsignaturaProfesorForm.propTypes = {
  idAsig: PropTypes.string,
  opcionesAsigAll: PropTypes.array,
  opcionesAsig: PropTypes.array,
  opcionesProf: PropTypes.array,
  profesorAsignaturaStash: PropTypes.array.isRequired,
  setProfesorAsignaturaStash: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  oldValues: PropTypes.object,
};

export default AsignaturaProfesorForm;
